import { Alert, Button, Stack, Tooltip } from "@mui/material";
import React, { useMemo, useState } from "react";
import SelectModeBanner from "../SelectModeBanner";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { DbShareMediaInfos } from "../../../Core/Api/Types/db-share-media-infos";
import { shareService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { useTranslation } from "react-i18next";

export interface ShareBannerProps {
  handleCloseClick: React.MouseEventHandler;
  shares: DbShareMediaInfos[];
}

const ShareBanner: React.FC<ShareBannerProps> = (props) => {
  const { t } = useTranslation();
  const { handleCloseClick, shares } = props;
  const [openTooltipAddViews, setOpenTooltipAddViews] = useState(false);
  const [openTooltipAddDays, setOpenTooltipAddDays] = useState(false);
  const customSnackbar = useCustomSnackbar();

  const handleDelete = () => {
    shares.forEach((share) => {
      shareService.DeleteShareById(share.id).then((response) => {
        if (response && response.success)
          customSnackbar(t("alert:success.shareWithNameDeletd", { name: share.name }), "success");
        else
          customSnackbar(
            t("alert:error.deletingShareWithName", { name: share.name, message: response.message }),
            "error"
          );
      });
    });
  };

  const isOk = useMemo(() => {
    if (shares.filter((x) => x.isMultiple).length > 0 && shares.filter((x) => !x.isMultiple).length > 0) return false;

    return true;
  }, [shares]);

  const _renderTooltip = (type: "views" | "days") => {
    const formId = "share-edit-form";
    const [value, setValue] = useState(1);
    const formValid = useAppSelector(GetFormIsValid(formId));

    const handleUpdate = () => {
      shares.forEach((share) => {
        shareService
          .UpdateShare({
            shareId: share.id,
            maxDays: type === "days" ? value : 0,
            maxViews: type === "views" ? value : 0,
            maxIPs: null,
            maxClipsNumber: null,
            maxClipsDuration: null,
            watermarkTemplate: null,
            watermarkText: null,
          })
          .then((response) => {
            if (response && response.success)
              customSnackbar(t("alert:success.shareWithNameUpdated", { name: share.name }), "success");
            else
              customSnackbar(
                t("alert:error.updatingShareWithName", { name: share.name, message: response.message }),
                "error"
              );
          });

        setOpenTooltipAddViews(false);
        setOpenTooltipAddDays(false);
      });
    };

    return (
      <Stack sx={{ width: 150 }} pt={0.5} pb={0.5} spacing={1}>
        <FormInputNumber
          label={type === "views" ? t("field:label.selectViewsToAdd") : t("field:label.selectDaysToAdd")}
          startAdornment={type === "views" ? <AddIcon /> : <AccessTimeIcon />}
          value={value}
          onChange={(e) => {
            setValue(parseInt(e.currentTarget.value));
          }}
          validationProps={{
            formId: formId,
            formSectionId: "section",
            fieldId: "value",
            required: true,
            minValue: 1,
            maxValue: 300,
            initialState: true,
            notDecimal: true,
            positive: true,
          }}
          stackProps={{}}
        />
        <Button variant="contained" startIcon={<DoneIcon />} onClick={handleUpdate} disabled={!formValid}>
          {t("common:submit")}
        </Button>
      </Stack>
    );
  };

  return (
    <SelectModeBanner text={t("banner:text.sharesSelected", { count: shares.length })} onCloseClick={handleCloseClick}>
      {!isOk && (
        <Alert icon={<WarningIcon />} color="warning" variant="outlined" sx={{ mr: 2 }}>
          {t("alert:warning.bulkActionsNotAllowed")}
        </Alert>
      )}
      <Tooltip
        PopperProps={{
          disablePortal: false,
        }}
        onClose={() => setOpenTooltipAddViews(false)}
        open={openTooltipAddViews}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={_renderTooltip("views")}
        placement="bottom"
        arrow
      >
        <Button
          variant="whiteSoft"
          startIcon={<AddIcon />}
          onClick={() => setOpenTooltipAddViews((prev) => !prev)}
          disabled={!isOk || !UserHasAcl(AclTypes.aclShareUpdate)}
        >
          {t("button:addMoreViews")}
        </Button>
      </Tooltip>

      <Tooltip
        PopperProps={{
          disablePortal: false,
        }}
        onClose={() => setOpenTooltipAddDays(false)}
        open={openTooltipAddDays}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={_renderTooltip("days")}
        placement="bottom"
        arrow
      >
        <Button
          variant="whiteSoft"
          startIcon={<AccessTimeIcon />}
          onClick={() => setOpenTooltipAddDays((prev) => !prev)}
          disabled={!isOk || !UserHasAcl(AclTypes.aclShareUpdate)}
        >
          {t("button:addMoreDays")}
        </Button>
      </Tooltip>

      <Button
        variant="contained"
        color="error"
        startIcon={<DeleteIcon />}
        onClick={handleDelete}
        disabled={!isOk || !UserHasAcl(AclTypes.aclShareDelete)}
      >
        {t("common:delete")}
      </Button>
    </SelectModeBanner>
  );
};

export default ShareBanner;
